import * as amplitude from "@amplitude/analytics-browser";
import { datadogLogs } from "@datadog/browser-logs";
import type { CustomerPortalRpcClient } from "@redotech/customer-portal-sdk/rpc/client";
import { useTriggerLoad } from "@redotech/react-util/load";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { getCompensationMethod } from "@redotech/redo-customer-portal-shared/return-review/util";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  ReturnTotalCalculations as Calculations,
  DraftReturn,
  isDraftingExchangeReturn,
  ShipmentMethodName,
} from "@redotech/redo-model/draft-return/draft-return";
import { friendlyErrorMessage } from "@redotech/redo-model/draft-return/errors/friendly-error-message";
import {
  friendlySubmitReturnErrorMessages,
  SubmitReturnErrorCode,
} from "@redotech/redo-model/draft-return/errors/submit-return";
import { getShipmentMethod } from "@redotech/redo-model/draft-return/util";
import { Resource } from "@redotech/redo-model/localization/resource";
import { Channel } from "@redotech/redo-model/return";
import { ProvisionType } from "@redotech/redo-model/return-flow";
import { THIRD_PARTY_SOURCE } from "@redotech/redo-model/third-party";
import { ClientError } from "@redotech/rpc/errors";
import { CardElement } from "@stripe/react-stripe-js";
import type { Stripe, StripeElements } from "@stripe/stripe-js";
import { HttpStatusCode } from "axios";
import { memo, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  InstantExchangePaymentCard,
  LabelPaymentCard,
  ReturnInStoreCard,
  SummaryCard,
} from ".";
import * as review from "../review.module.css";
import { ContactRedoModal } from "./contact-redo-modal";
import { PickupCard } from "./shipment-method-cards/pickup-card";
import { SettlementCard } from "./shipment-method-cards/settlement-card";

const isPaymentProblem = (code: SubmitReturnErrorCode) =>
  [
    SubmitReturnErrorCode.INVALID_PAYMENT_TOKEN,
    SubmitReturnErrorCode.SHIPPING_CHARGE_FAILED,
    SubmitReturnErrorCode.UPSELL_CHARGE_FAILED,
  ].includes(code);
export const FeeAndSummary = memo(function FeeAndSummary({
  draftReturn,
  settings,
  stripe,
  newOrderMethod,
  copyResource,
  client,
  creatingStripePayment,
  setCreatingStripePayment,
  calculations,
  showPickup,
  showInStoreReturn,
  showSettlement,
  setDraftReturn,
  callback,
}: {
  draftReturn: DraftReturn;
  settings: ReturnAppSettings;
  stripe: Stripe;
  newOrderMethod: ProvisionType;
  copyResource: Resource;
  client: CustomerPortalRpcClient;
  creatingStripePayment: boolean;
  setCreatingStripePayment: (creating: boolean) => void;
  calculations: Calculations;
  showPickup: boolean;
  showInStoreReturn: boolean;
  showSettlement: boolean;
  setDraftReturn: (draftReturn: DraftReturn) => void;
  callback?: string;
}) {
  const size = useScreenType();
  const navigate = useNavigate();
  const compensationMethod = getCompensationMethod(draftReturn);
  const shipmentMethod = getShipmentMethod(draftReturn);

  const [elements, setElements] = useState<StripeElements | undefined>(
    undefined,
  );
  const [submitReturnErrorMessage, setSubmitReturnErrorMessage] = useState<
    string | undefined
  >();
  const [paymentInfoFilled, setPaymentInfoFilled] = useState<boolean>(false);

  const [submitReturnLoad, submitReturn] = useTriggerLoad(async (signal) => {
    if (!draftReturn) {
      return;
    }

    let token: string | null = null;
    if (shouldCollectPayment) {
      const card = elements?.getElement(CardElement);
      if (!stripe || !card || !paymentInfoFilled) {
        setSubmitReturnErrorMessage(
          "We expected payment information but none was provided, please contact Redo support.",
        );
        return;
      }
      setCreatingStripePayment(true);

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card,
        billing_details: { name: draftReturn.customer.name },
      });
      if (error) {
        console.error(error);
        setSubmitReturnErrorMessage(error.message ?? "");
        return;
      } else {
        token = paymentMethod.id;
      }
    }

    setCreatingStripePayment(false);
    let success = false;
    const start = Date.now();
    try {
      setSubmitReturnErrorMessage("");

      const { returnId } = settings.returns?.useNewSubmitReturn
        ? await client.submitDraftReturn(
            {
              draftReturnId: draftReturn._id,
              paymentToken: token,
              settlement: {
                accepted: settlementAccept,
                refund: settlementRefund,
              },
              channel: callback ? Channel.SHOP_APP : Channel.WEB,
            },
            { signal },
          )
        : await client.submitReturn(
            {
              draftReturnId: draftReturn._id,
              provisionType: newOrderMethod,
              paymentToken: token,
              settlement: {
                accepted: settlementAccept,
                refund: settlementRefund,
              },
              channel: callback ? Channel.SHOP_APP : Channel.WEB,
              customerTimeZone: Temporal.Now.timeZoneId(),
            },
            { signal },
          );

      success = true;

      if (
        draftReturn.orders.find(
          (order) => order.salesChannel === THIRD_PARTY_SOURCE,
        )
      ) {
        navigate(`/returns-portal/warranties/${returnId}`);
      } else {
        let shopAppLink = "";
        if (callback) {
          shopAppLink = `?shopAppLink=${callback}`;
        }
        navigate(`/returns-portal/orders/return/${returnId}${shopAppLink}`);
      }
    } catch (error) {
      setSubmitReturnErrorMessage(
        friendlyErrorMessage(error, friendlySubmitReturnErrorMessages),
      );

      const justAPaymentError =
        error instanceof ClientError &&
        (error.statusCode === HttpStatusCode.PaymentRequired ||
          (error.code &&
            isPaymentProblem(error.code as SubmitReturnErrorCode)));

      /** Don't log to datadog, don't throw (which would open the support modal) */
      if (justAPaymentError) {
        console.error(error);
        return;
      }

      datadogLogs.logger.error("Error submitting return", {
        error,
        draftReturn,
      });
      throw error;
    } finally {
      amplitude.logEvent("submit-return", {
        success,
        returnType: draftReturn.returnType,
        rejected: draftReturn.rejected ?? false,
        duration: Date.now() - start,
        shipmentMethod,
        isExchange: isDraftingExchangeReturn(draftReturn),
        eligibleForPickup: !!(pickupFee && !compensationMethod?.manualReview),
        pickup: shipmentMethod === ShipmentMethodName.PICKUP,
        pickupFee: pickupFee,
      });
    }
  });

  const {
    shippingFee,
    totalShippingFee,
    pickupFee,
    settlementAccept,
    settlementRefund,
    skipPayment,
    labelDeducted,
  } = calculations;

  /** N.B. totalShippingFee includes package pickup fee, see the return totals calculator */
  const needsToPayTotalShippingFee =
    totalShippingFee > 0 && !skipPayment && !labelDeducted;

  const shouldCollectPayment =
    newOrderMethod === ProvisionType.INSTANT || needsToPayTotalShippingFee;

  const errorMessageHref = useMemo(() => {
    const subject = `Submission error on ${settings.merchantName}: ${draftReturn.orders[0]?.name}`;
    const body = `I encountered an error while submitting a ${draftReturn.returnType}${
      submitReturnErrorMessage ? `: ${submitReturnErrorMessage}` : ""
    }.\n\nReference ID: ${draftReturn?._id}`;
    return `mailto:support@getredo.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [
    draftReturn._id,
    draftReturn.orders,
    draftReturn.returnType,
    settings.merchantName,
    submitReturnErrorMessage,
  ]);

  //If we are in a react native webview and payment is required, we must link out to a web browser
  useEffect(() => {
    const isInWebView = (window as any).ReactNativeWebView !== undefined;

    async function linkOut() {
      //We need to set the provision type to instant on the draft return for the new window
      if (
        newOrderMethod === ProvisionType.INSTANT &&
        draftReturn.provisionType !== ProvisionType.INSTANT
      ) {
        await client.setProvisionType({
          draftReturnId: draftReturn._id,
          provisionType: ProvisionType.INSTANT,
        });
      }
      const orderId = draftReturn.orders[0]?.id;
      const orderName = draftReturn.orders[0]?.name.replace("#", "");
      const email = draftReturn.customer.shippingAddress?.email;
      const draftReturnId = draftReturn._id;

      const end = `returns-portal/orders/${orderId}/return?email=${email}&order=${orderName}&shopDraftReturnId=${draftReturnId}`;

      const fullUrl = `https://${settings.storeUrl}/apps/redo/${end}`;

      window.open(fullUrl, "_blank");
    }
    if (isInWebView && shouldCollectPayment) {
      void linkOut();
    }
  }, [
    shouldCollectPayment,
    pickupFee,
    shippingFee,
    skipPayment,
    compensationMethod,
    newOrderMethod,
    draftReturn,
    settings.storeUrl,
    client,
  ]);

  return (
    <div className={review.summaryCards}>
      {size === ScreenType.MOBILE &&
        showPickup &&
        draftReturn?.shipmentMethod?.method === ShipmentMethodName.PICKUP && (
          <PickupCard pickup={draftReturn?.shipmentMethod.pickupData} />
        )}
      {size === ScreenType.MOBILE && showSettlement && (
        <SettlementCard
          client={client}
          draftReturn={draftReturn}
          orderValue={compensationMethod.totalReturnableValue}
          setDraftReturn={setDraftReturn}
          settlementRefund={settings?.returns?.settlementRefund ?? 0}
        />
      )}
      {size === ScreenType.MOBILE && showInStoreReturn && (
        <ReturnInStoreCard
          client={client}
          draftReturn={draftReturn}
          setDraftReturn={setDraftReturn}
        />
      )}
      {/* Hide separate label payment card if it is deducted from refund or store credit */}
      {!labelDeducted && (
        <LabelPaymentCard
          calculations={calculations}
          draftReturn={draftReturn}
          newOrderMethod={newOrderMethod}
          setElements={setElements}
          setPaymentInfoFilled={setPaymentInfoFilled}
          settings={settings}
          shipmentMethod={shipmentMethod}
          skipPayment={skipPayment}
          stripe={stripe}
          submitReturn={submitReturn}
        />
      )}
      {/* Shown for instant exchange and no shipping fee to explain why we need the card */}
      {(skipPayment || labelDeducted) &&
        newOrderMethod === ProvisionType.INSTANT && (
          <InstantExchangePaymentCard
            calculations={calculations}
            setElements={setElements}
            setPaymentInfoFilled={setPaymentInfoFilled}
            settings={settings}
            stripe={stripe}
            submitReturn={submitReturn}
          />
        )}
      {/* Note: This is just displaying the message. To open the support modal, throw in `submitReturnLoad` */}
      {submitReturnErrorMessage && (
        <ErrorText errorMessage={submitReturnErrorMessage} />
      )}
      <SummaryCard
        calculations={calculations}
        copyResource={copyResource}
        creatingStripePayment={creatingStripePayment}
        draftReturn={draftReturn}
        elements={elements}
        newOrderMethod={newOrderMethod}
        paymentInfoFilled={paymentInfoFilled}
        settings={settings}
        stripe={stripe}
        submitReturn={submitReturn}
        submitReturnLoad={submitReturnLoad}
      />
      {submitReturnLoad.error ? (
        <ContactRedoModal
          errorMessage={submitReturnErrorMessage}
          errorMessageHref={errorMessageHref}
          title="Submission error"
        />
      ) : null}
    </div>
  );
});

const ErrorText = memo(function ErrorText({
  errorMessage,
}: {
  errorMessage: string;
}) {
  return <div className={review.error}>{errorMessage}</div>;
});
